import { NotificationManager as nm } from "react-notifications";
import { get, post, loginPost, download } from "shared/utils/request.js";
import { getApiEndpoint, getGraphQLEndpoint } from "shared/utils/env.js";
import { jsonToParams, jsonToGraphQLParams } from "shared/utils/graphql.js";

const defaultHandleSuccess = (h, v) => {
  if (h) {
    h(v);
  }
};

const defaultHandleError = (h, e) => {
  if (h) {
    h(e);
  } else {
    nm.warning("An error happened while requesting the API");
  }
};

/* login */

export const login = ({ handleSuccess = null, handleError = null, body }) =>
  loginPost(
    getApiEndpoint() + "api/login",
    body,
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
    true,
  );

export const logout = ({ handleSuccess = null, handleError = null }) =>
  loginPost(
    getApiEndpoint() + "api/logout",
    null,
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
    true,
  );

/* NONCE */

export const getGenerateNonce = ({ handleSuccess = null, handleError = null }) =>
  get(
    getApiEndpoint() + "api/generate_nonce",
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

/* Export */

export const downloadExport = (filename, handleSuccess = null, handleError = null) =>
  download(
    getApiEndpoint() + "api/export/" + filename,
    filename,
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
    true,
  );

/* GRAPHQL */

/* User */

export const getLoggedUser = ({ handleSuccess = null, handleError = null }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `
      {
        getLoggedUser {
          id,
          address,
          email,
          isEmailConfirmed,
          premiumEndDate
        }
      }
    `,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const updateLoggedUserEmail = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `
      mutation {
        updateLoggedUserEmail(email: "${params.email}") {
          user {
            address,
            email
          }
        }
      }
    `,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const sendConfirmationMail = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `
      mutation {
        sendConfirmationMail(address: "${params.address}", email: "${params.email}") {
          user {
            address,
            email
          }
        }
      }
    `,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

/* Notification */

export const getNotificationScopesAndNotifications = ({ handleSuccess = null, handleError = null }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getNotificationScopes {
          id,
          user {
            id
          },
          type,
          positions,
          primaryPositionOnly,
          nationalities,
          minPrice,
          maxPrice,
          minAge,
          maxAge,
          minOvr,
          maxOvr,
          minPac,
          maxPac,
          minDri,
          maxDri,
          minPas,
          maxPas,
          minSho,
          maxSho,
          minDef,
          maxDef,
          minPhy,
          maxPhy,
          percentBelowEstimation,
          unitBelowEstimation,
          retiredInXYears,
          ovrBonus,
          ovrBonusPositions
        },
        getNotifications(order: -1, type: "player") {
          id,
          status,
          playerIds,
          clubIds,
          creationDate,
          sendingDate,
          notificationScope {
            id
          },
          clubNotificationScope {
            id
          }
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getNotificationsOfNotificationScope = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getNotifications(${jsonToParams(params)}) {
          id,
          status,
          playerIds,
          clubIds,
          creationDate,
          sendingDate,
          notificationScope {
            id
          },
          clubNotificationScope {
            id
          }
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const addNotificationScope = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `
      mutation {
        addNotificationScope(${jsonToParams(params)}) {
          notificationScope {
            id,
            type
          }
        }
      }
    `,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const updateNotificationScope = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `
      mutation {
        updateNotificationScope(${jsonToParams(params)}) {
          notificationScope {
            id,
            type
          }
        }
      }
    `,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const deleteNotificationScope = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `
      mutation {
        deleteNotificationScope(${jsonToParams(params)}) {
          notificationScope {
            id
          }
        }
      }
    `,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getClubNotificationScopesAndNotifications = ({ handleSuccess = null, handleError = null }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getClubNotificationScopes {
          id,
          type,
          minPrice,
          maxPrice,
          countries,
          cities,
          divisions
        },
        getNotifications(order: -1, type: "club") {
          id,
          status,
          playerIds,
          creationDate,
          sendingDate,
          clubNotificationScope {
            id
          }
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getNotificationsOfClubNotificationScope = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getNotifications(${jsonToParams(params)}) {
          id,
          status,
          playerIds,
          clubIds,
          creationDate,
          sendingDate,
          clubNotificationScope {
            id
          }
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const addClubNotificationScope = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `
      mutation {
        addClubNotificationScope(${jsonToParams(params)}) {
          clubNotificationScope {
            id,
            type
          }
        }
      }
    `,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const deleteClubNotificationScope = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `
      mutation {
        deleteClubNotificationScope(${jsonToParams(params)}) {
          clubNotificationScope {
            id
          }
        }
      }
    `,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

/* Report */

export const getReportConfigurations = ({ handleSuccess = null, handleError = null }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getReportConfigurations {
          id,
          type,
          time
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const addReportConfiguration = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `
      mutation {
        addReportConfiguration(${jsonToParams(params)}) {
          reportConfiguration {
            id,
            type,
            time
          }
        }
      }
    `,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const updateReportConfiguration = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `
      mutation {
        updateReportConfiguration(${jsonToParams(params)}) {
          status
        }
      }
    `,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const deleteReportConfiguration = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `
      mutation {
        deleteReportConfiguration(${jsonToParams(params)}) {
          status
        }
      }
    `,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

/* Clubs */

export const getClubs = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getClubs(${jsonToParams(params)}) {
          id,
          type,
          status,
          name,
          division,
          city,
          country,
          b11,
          b11a,
          b16a,
          ig11,
          playerCount,
          mmr,
          owner { id },
          geolocation {
            city,
            country,
            latitude,
            longitude
          }
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getClubData = ({ handleSuccess = null, handleError = null }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getClubCount(foundedOnly: true),
        getAllClubCount: getClubCount(foundedOnly: false),
        getClubOwnerCount,
        getClubsPerOwnerCounts(foundedOnly: true) {
          key,
          count
        },
        getAllClubsPerOwnerCounts: getClubsPerOwnerCounts(foundedOnly: false) {
          key,
          count
        },
        getClubDivisionCounts(foundedOnly: true) {
          key,
          count
        },
        getAllClubDivisionCounts: getClubDivisionCounts(foundedOnly: false) {
          key,
          count
        },
        getDataPoints(property: "founded_club_count") {
          date,
          value
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getUserData = ({ handleSuccess = null, handleError = null }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getClubUserData: getDataPoints(property: "club_owner_count") {
          date,
          value
        }
        getActiveArenaUserData: getDataPoints(property: "active_arena_manager_count_7d") {
          date,
          value
        }
        getActiveArenaUserData24h: getDataPoints(property: "active_arena_manager_count_24h") {
          date,
          value
        }
        getActiveArenaUserData30d: getDataPoints(property: "active_arena_manager_count_30d") {
          date,
          value
        }
        getPlayerUserData: getDataPoints(property: "player_owner_count") {
          date,
          value
        }
        getStaffOnlyUserData: getDataPoints(property: "staff_only_count") {
          date,
          value
        }
        getPlayerWithoutClubUserData: getDataPoints(property: "player_owner_without_club_count") {
          date,
          value
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getMarketplaceData = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getPlayerSaleTotal: getDataPoints(property: "${params.saleTotalProperty || params.playerSaleTotalProperty}") {
          date,
          value
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getActiveCompetitionsByDivision = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getActiveCompetitionsByDivision(divisionName: "${params.divisionName}") {
          id,
          season,
          seasonName,
          name,
          status,
          type,
          lastComputationDate,
          startingDate,
          participants {
            id,
            status,
            name,
            division,
            city,
            country,
            b11,
            b11a,
            b16a,
            ig11,
            playerCount,
            mmr
          }
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getCompetitionsByDivisionAndSeason = ({
  handleSuccess = null,
  handleError = null,
  params,
  startDate = null,
  endDate = null,
}) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getCompetitionsByDivisionAndSeason(divisionName: "${params.divisionName}", seasonName: "${params.seasonName}") {
          id,
          season,
          seasonName,
          name,
          status,
          type,
          lastComputationDate,
          startingDate,
          participants {
            id,
            status,
            name,
            division,
            city,
            country,
            b11,
            b11a,
            b16a,
            ig11,
            playerCount,
            mmr,
            history(
              startDate: "${startDate}",
              endDate:   "${endDate}"
            ) {
              date,
              b11,
              b11a,
              b16a,
              ig11,
              mmr,
              playerCount
            }
          }
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getClubMarketplaceData = ({ handleSuccess = null, handleError = null, params = {} }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getClubSales: getSales(type: "CLUB", limit: ${params.limit || 10000}, skip: ${params.skip || 0}, sort: "execution_date", order: -1) {
          executionDate,
          price,
          club {
            name,
            division,
            city,
            country
          }
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getPlayerDashboardData = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getPlayerCount: getPlayerCount(${jsonToParams(params)}),
        getPlayerOwnerCount
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getPlayerDashboardDataByCriteria = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getPlayerCountByCriteria(${jsonToParams(params)}) {
          NAT { key count }
          AGE { key count }
          OVR { key count }
          POS { key count }
          FOOT { key count }
          HEI { key count }
          PAC { key count }
          DRI { key count }
          PAS { key count }
          SHO { key count }
          DEF { key count }
          PHY { key count }
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getPlayerCountPerCountry = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
          getPlayerCountPerCountry(${jsonToParams(params)}) {
            key,
            count
          },
        }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getClubCountPerGeolocation = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
          getClubCountPerGeolocation(${jsonToParams(params)}) {
            count,
            geolocation {
              country,
              city,
              latitude,
              longitude
            }
          },
        }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getUserCountPerGeolocation = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
          getUserCountPerGeolocation(${jsonToParams(params)}) {
            count,
            geolocation {
              country,
              city,
              latitude,
              longitude
            }
          },
        }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getTeams = ({ handleSuccess = null, handleError = null }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getTeams {
          id,
          name,
          position,
          formation,
          isPublic,
          teamMembers {
            id,
            player {
              id,
              firstName,
              lastName,
              age,
              retirementYears,
              height,
              overall,
              nationalities,
              positions,
              pace,
              shooting,
              dribbling,
              passing,
              defense,
              physical,
              goalkeeping,
              resistance,
              owner {
                id,
                name
              }
            },
            position
          }
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getTeamMembers = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getTeamMembers(${jsonToParams(params)}) {
          id,
          team {
            id
          },
          player {
            id,
            firstName,
            lastName,
            age,
            retirementYears,
            height,
            overall,
            nationalities,
            positions,
            pace,
            shooting,
            dribbling,
            passing,
            defense,
            physical,
            goalkeeping,
            resistance
          },
          position
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const addTeam = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `
      mutation {
        addTeam(${jsonToParams(params)}) {
          team {
            id,
            name,
            formation,
            isPublic
          }
        }
      }
    `,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const updateTeam = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `
      mutation {
        updateTeam(${jsonToParams(params)}) {
          status
        }
      }
    `,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const deleteTeam = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `
      mutation {
        deleteTeam(${jsonToParams(params)}) {
          status
        }
      }
    `,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const addTeamMembers = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `
      mutation {
        addTeamMembers(${jsonToParams(params)}) {
          teamMembers {
            team { id },
            player { id },
            position
          }
        }
      }
    `,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const updateTeamMember = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `
      mutation {
        updateTeamMember(${jsonToParams(params)}) {
          status
        }
      }
    `,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const updateTeamMembers = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `
      mutation {
        updateTeamMembers(teamId: "${params.teamId}", updates: ${jsonToGraphQLParams(params.members)}) {
          status
        }
      }
    `,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const moveTeam = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `
      mutation {
        moveTeam(${jsonToParams(params)}) {
          status
        }
      }
    `,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const deleteTeamMember = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `
      mutation {
        deleteTeamMember(${jsonToParams(params)}) {
          status
        }
      }
    `,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getPlayerNationalities = ({ handleSuccess = null, handleError = null }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getPlayerNationalities
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getClubCountriesAndCities = ({ handleSuccess = null, handleError = null }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getClubCountries,
        getClubCities
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getPlayers = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getPlayers(${jsonToParams(params)}) {
          id,
          firstName,
          lastName,
          age,
          retirementYears,
          height,
          overall,
          nationalities,
          positions,
          pace,
          shooting,
          dribbling,
          passing,
          defense,
          physical,
          goalkeeping,
          resistance
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getCompetitions = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getCompetitions(${jsonToParams(params)}) {
          id,
          season,
          seasonName,
          name,
          status,
          type,
          lastComputationDate,
          startingDate
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getFullCompetitions = ({
  handleSuccess = null,
  handleError = null,
  params,
  startDate = null,
  endDate = null,
}) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getCompetitions(${jsonToParams(params)}) {
          id,
          season,
          seasonName,
          name,
          status,
          type,
          lastComputationDate,
          startingDate,
          participants {
            id,
            status,
            name,
            division,
            city,
            country,
            b11,
            b11a,
            b16a,
            ig11,
            playerCount,
            mmr,
            history(
              startDate: ${startDate},
              endDate:   ${endDate}
            ) {
              date,
              b11,
              b11a,
              b16a,
              ig11,
              mmr,
              playerCount
            }
          },
          groups {
            group
            clubIds
          },
          rankingHistory {
            date,
            rankings {
              clubId,
              wins,
              draws,
              losses,
              points,
              goals,
              goalsAgainst,
              played,
              trophyProbability,
              promotionProbability,
              relegationProbability
            }
          }
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getFullCompetitionsLimited = ({
  handleSuccess = null,
  handleError = null,
  params,
  startDate = null,
  endDate = null,
}) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getCompetitions(${jsonToParams(params)}) {
          id,
          season,
          seasonName,
          name,
          status,
          type,
          lastComputationDate,
          startingDate,
          participants {
            id,
            status,
            name,
            division,
            city,
            country,
            b11,
            b11a,
            b16a,
            ig11,
            playerCount,
            mmr,
            history(
              startDate: "2026-05-01T00:00:00Z",
              endDate: "2026-09-01T00:00:00Z"
            ) {
              date,
              b11,
              b11a,
              b16a,
              ig11,
              mmr,
              playerCount
            }
          },
          groups {
            group
            clubIds
          },
          rankingHistory {
            date,
            rankings {
              clubId,
              wins,
              draws,
              losses,
              points,
              goals,
              goalsAgainst,
              played,
              trophyProbability,
              relegationProbability,
              promotionProbability
            }
          }
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getUsers = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getUsers(${jsonToParams(params)}) {
          id,
          address,
          name,
          country,
          city,
          geolocation {
            country,
            city,
            latitude,
            longitude
          }
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getContracts = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getContracts(${jsonToParams(params)}) {
          id,
          status,
          revenueShare,
          totalRevenueShareLocked,
          startSeason,
          numberOfSeason,
          autoRenewal,
          creationDate,
          club {
            id,
            name,
            status,
            division,
            city,
            country,
            foundationDate
          },
          player {
            id,
            firstName,
            lastName,
            age,
            retirementYears,
            height,
            overall,
            nationalities,
            positions,
            pace,
            shooting,
            dribbling,
            passing,
            defense,
            physical,
            goalkeeping,
            resistance
          }
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getPlayerSales = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getPlayerSales: getSales(${jsonToParams(params)}) {
          executionDate,
          price,
          age,
          overall,
          positions,
          player {
            id,
            firstName,
            lastName,
            age,
            retirementYears,
            height,
            overall,
            nationalities,
            positions,
            pace,
            shooting,
            dribbling,
            passing,
            defense,
            physical,
            goalkeeping,
            resistance
          }
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getPackSales = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getPlayerSales: getSales(${jsonToParams(params)}) {
          executionDate,
          price,
          pack {
            id,
            name,
            type
          }
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getPlayerPricingHistory = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getPlayerPricingHistory(${jsonToParams(params)}) {
          overall,
          age,
          price,
          position,
          date
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getPlayerPricings = ({ handleSuccess = null, handleError = null }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getPlayerPricings {
          overall,
          age,
          price,
          position
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getPlayerYearValuations = ({ handleSuccess = null, handleError = null }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getPlayerYearValuations {
          overall,
          price
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getRawPlayerPricings = ({ handleSuccess = null, handleError = null }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getRawPlayerPricings {
          overall,
          age,
          price,
          position,
          date
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getFormationMetaEngines = ({ handleSuccess = null, handleError = null }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
          getFormationMetaEngines
        }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getFormationMetas = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
          getFormationMetas(${jsonToParams(params)}) {
            type,
            formation1,
            formation2,
            victories,
            draws,
            defeats,
            goalsScored,
            goalsConceded,
            engine
          }
        }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getFormations = ({ handleSuccess = null, handleError = null }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getFormations
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getOpponents = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
          getOpponents(${jsonToParams(params)}) {
            id,
            startDate,
            status,
            type,
            homeScore,
            awayScore,
            homeFormation,
            awayFormation,
            homeOverall,
            awayOverall,
            homePositions {
              index,
              player
            },
            awayPositions {
              index,
              player
            },
            modifiers {
              target {
                type,
                ids
              },
              values {
                type,
                value,
                field
              }
            },
            players,
            homeClub {
              id,
              type,
              status,
              name,
              division,
              city,
              country,
              b11,
              b11a,
              b16a,
              ig11,
              playerCount,
              mmr
            },
            awayClub {
              id,
              type,
              status,
              name,
              division,
              city,
              country,
              b11,
              b11a,
              b16a,
              ig11,
              playerCount,
              mmr
            }
          }
        }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getOverallVsGdRates = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getOverallVsGdRates(${jsonToParams(params)}) {
          overallDifference,
          goalDifference,
          rate,
          engine
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getMatches = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
          getMatches(${jsonToParams(params)}) {
            id,
            competitionId,
            startDate,
            status,
            type,
            homeScore,
            awayScore,
            homeFormation,
            awayFormation,
            homeOverall,
            awayOverall,
            homePositions {
              index,
              player
            },
            awayPositions {
              index,
              player
            },
            modifiers {
              target {
                type,
                ids
              },
              values {
                type,
                value,
                field
              }
            },
            players,
            homeClub {
              id,
              status,
              name,
              division,
              city,
              country,
              b11,
              b11a,
              b16a,
              ig11,
              playerCount,
              mmr
            },
            awayClub {
              id,
              status,
              name,
              division,
              city,
              country,
              b11,
              b11a,
              b16a,
              ig11,
              playerCount,
              mmr
            }
          }
        }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getFormationMatches = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
          getMatches(${jsonToParams(params)}) {
            id,
            startDate,
            status,
            type,
            homeScore,
            awayScore,
            homeFormation,
            awayFormation,
            homeOverall,
            awayOverall,
            homeClub { id, name, division, city, country },
            awayClub { id, name, division, city, country }
          }
        }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getPackFields = ({ handleSuccess = null, handleError = null }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getPackFields {
          names
          types
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getTeamNotesOfUser = ({ handleSuccess = null, handleError = null }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getTeamNotesOfUser {
          id,
          note,
          team {
            id
          }
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const addTeamNote = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `
      mutation {
        addTeamNote(${jsonToParams(params)}) {
          teamNote {
            id
          }
        }
      }
    `,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const deleteTeamNote = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `
      mutation {
        deleteTeamNote(${jsonToParams(params)}) {
          status
        }
      }
    `,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const getMatchDossiers = ({ handleSuccess = null, handleError = null }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `{
        getMatchDossiers {
          id,
          name,
          position,
          matches
        }
      }`,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const addMatchDossier = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `
      mutation {
        addMatchDossier(${jsonToParams(params)}) {
          matchDossier {
            id,
            name
          }
        }
      }
    `,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const updateMatchDossier = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `
      mutation {
        updateMatchDossier(${jsonToParams(params)}) {
          status
        }
      }
    `,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const deleteMatchDossier = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `
      mutation {
        deleteMatchDossier(${jsonToParams(params)}) {
          status
        }
      }
    `,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );

export const moveMatchDossier = ({ handleSuccess = null, handleError = null, params }) =>
  post(
    getGraphQLEndpoint(),
    JSON.stringify({
      query: `
      mutation {
        moveMatchDossier(${jsonToParams(params)}) {
          status
        }
      }
    `,
    }),
    (v) => defaultHandleSuccess(handleSuccess, v),
    (e) => defaultHandleError(handleError, e),
  );
